// src/components/icons.tsx — the icon set, drawn inline.
//
// The prototype uses lucide-react. This app draws its own dozen-and-a-half
// glyphs instead, for one reason: everything in the bundle has to arrive over
// a VPN from behind the GFW, and an icon library is a dependency, a build step
// and a few tens of kilobytes for what amounts to eighteen paths. They are
// deliberately in lucide's visual language — 24×24 box, 1.6 stroke, round
// caps, currentColor — so they sit next to the prototype's screenshots
// without looking like a different app.

import type { SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  size?: number;
  /** Stroke weight. Bumped for the selected tab, as in the prototype. */
  weight?: number;
}

function Icon({ size = 20, weight = 1.6, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={weight}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const HomeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 10.5 12 3.5l8.5 7V20a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1z" />
    <path d="M9.5 21v-6h5v6" />
  </Icon>
);

export const ReceiptIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 3h12v18l-3-1.8-3 1.8-3-1.8L6 21z" />
    <path d="M9.5 8.5h5M9.5 12.5h5" />
  </Icon>
);

export const SwapIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 8.5h15M16 5.5l3 3-3 3" />
    <path d="M20 15.5H5M8 12.5l-3 3 3 3" />
  </Icon>
);

export const UsersIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9.5" cy="8" r="3.5" />
    <path d="M3 19.5a6.5 6.5 0 0 1 13 0" />
    <path d="M16 5a3.5 3.5 0 0 1 0 6.5M17.5 14.2a6 6 0 0 1 3.5 5.3" />
  </Icon>
);

export const PlusIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const SearchIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-3.6-3.6" />
  </Icon>
);

export const XIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 6 12 12M18 6 6 18" />
  </Icon>
);

export const CheckIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m4.5 12.5 5 5 10-11" />
  </Icon>
);

export const ChevronRightIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m9.5 6 6 6-6 6" />
  </Icon>
);

export const ChevronLeftIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m14.5 6-6 6 6 6" />
  </Icon>
);

export const ChevronDownIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 9.5 6 6 6-6" />
  </Icon>
);

export const UtensilsIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7.5 3v5.5M10.5 3v5.5M9 8.5V21" />
    <path d="M16.5 3c1.7 1.6 2.3 3.6 2.3 5.6 0 1.7-.8 2.9-2.3 3.4V21" />
  </Icon>
);

export const CarIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 15.5v-2.2c0-.4.1-.8.3-1.1l1.8-3.3A2 2 0 0 1 8.8 8h6.4a2 2 0 0 1 1.7 1l1.8 3.3c.2.3.3.7.3 1.1v2.2" />
    <path d="M3.5 15.5h17" />
    <circle cx="8" cy="16.5" r="1.6" />
    <circle cx="16" cy="16.5" r="1.6" />
  </Icon>
);

export const BedIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 19v-6.5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2V19" />
    <path d="M3 15.5h18" />
    <path d="M6.5 10.5V9a1 1 0 0 1 1-1h2.5a1 1 0 0 1 1 1v1.5" />
    <path d="M13 10.5V9a1 1 0 0 1 1-1h2.5a1 1 0 0 1 1 1v1.5" />
  </Icon>
);

export const TicketIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 9V7.5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1V9a2.6 2.6 0 0 0 0 6v1.5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V15a2.6 2.6 0 0 0 0-6Z" />
    <path d="M12 7.5v2M12 11v2M12 14.5v2" />
  </Icon>
);

export const BagIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5.5 8h13l-1 12.5h-11z" />
    <path d="M9 8V6.2a3 3 0 0 1 6 0V8" />
  </Icon>
);

export const TagIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M11.6 3.5H20a.5.5 0 0 1 .5.5v8.4a1 1 0 0 1-.3.7l-7.6 7.6a1 1 0 0 1-1.4 0l-7.7-7.7a1 1 0 0 1 0-1.4l7.6-7.6a1 1 0 0 1 .5-.5Z" />
    <circle cx="16.5" cy="7.5" r="1.3" />
  </Icon>
);

export const RefreshIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20.5 11.5a8.5 8.5 0 0 0-14.6-5.4L3 9" />
    <path d="M3 4.5V9h4.5" />
    <path d="M3.5 12.5a8.5 8.5 0 0 0 14.6 5.4L21 15" />
    <path d="M21 19.5V15h-4.5" />
  </Icon>
);

export const TrashIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 6.5h15" />
    <path d="M9.5 6.5V5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1.5" />
    <path d="M6.5 6.5 7.4 19a1 1 0 0 0 1 .9h7.2a1 1 0 0 0 1-.9l.9-12.5" />
    <path d="M10.5 10v6M13.5 10v6" />
  </Icon>
);

export const PencilIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 20.5l1-4.2L16.3 5a2 2 0 0 1 2.8 0l.9.9a2 2 0 0 1 0 2.8L8.7 19.5z" />
    <path d="m14.8 6.5 3.7 3.7" />
  </Icon>
);

export const CopyIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="9" y="9" width="11.5" height="11.5" rx="1.6" />
    <path d="M5.5 15H5a1.5 1.5 0 0 1-1.5-1.5v-8A1.5 1.5 0 0 1 5 4h8a1.5 1.5 0 0 1 1.5 1.5V6" />
  </Icon>
);

export const LogOutIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M15 4.5h3a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-3" />
    <path d="m10 8-4 4 4 4M6 12h10" />
  </Icon>
);

export const WarningIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4.5 21 20H3z" />
    <path d="M12 10v4M12 17h.01" />
  </Icon>
);

/** Maps an expense category to its glyph. Falls through to a tag. */
export const CATEGORY_ICONS: Record<string, (p: IconProps) => React.ReactElement> = {
  food: UtensilsIcon,
  transport: CarIcon,
  stay: BedIcon,
  activities: TicketIcon,
  shopping: BagIcon,
  other: TagIcon,
};

export function categoryIcon(key: string): (p: IconProps) => React.ReactElement {
  return CATEGORY_ICONS[key] ?? TagIcon;
}
